import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  geoJson2SvgDraw,
  geoJson2SvgDrawLabels,
  geoJson2SvgDrawPaths,
  GeoJson2SvgPolygonFeature,
  geoJson2SvgSetup,
} from '../../utilities/geojson2svg';
import * as turf from '@turf/turf';
import { SVG } from '@svgdotjs/svg.js';
import { useGeoJson2Svg } from '../../hooks/useGeojson2Svg';

export default function TestSvgPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    async function run() {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      const setup = await geoJson2SvgSetup(TEST_POLYGONS_2, PIXEL_DIMS, TEST_LABELS_2);
      canvas.width = setup.viewport.width;
      canvas.height = setup.viewport.height;

      const svg = SVG();
      svg.width(setup.viewport.width);
      svg.height(setup.viewport.height);

      await geoJson2SvgDrawPaths(svg, setup);
      await geoJson2SvgDrawLabels(svg, setup);
      await geoJson2SvgDraw(ctx, svg.svg(), setup);
    }

    run().catch(console.error);
  }, []);

  const [imageResult, setImageResult] = useState<string | null>(null);
  const geoJson2Svg = useGeoJson2Svg();
  const showJpg = useCallback(async () => {
    const dataUrl = await geoJson2Svg.generate(TEST_POLYGONS_2, PIXEL_DIMS, TEST_LABELS_2);
    setImageResult(dataUrl);
  }, [geoJson2Svg]);

  return (
    <>
      <canvas ref={canvasRef} />
      <button onClick={showJpg} type='button'>
        Preview
      </button>
      {geoJson2Svg.loading ? (
        <span>Loading...</span>
      ) : imageResult ? (
        <img src={imageResult} alt='SVG' />
      ) : null}
    </>
  );
}

const TEST_POLYGONS_1: GeoJson2SvgPolygonFeature[] = [
  turf.polygon(
    [
      [
        [144.98683615444583, -37.81981373411612],
        [144.98643805427736, -37.82120893333411],
        [144.98832762831728, -37.821018479121705],
        [144.98933689634936, -37.819140485695485],
        [144.98683615444583, -37.81981373411612],
      ],
      [
        [144.99005587754618, -37.81984019239263],
        [144.98966725775887, -37.82089702660991],
        [144.98837398207547, -37.82063030321058],
        [144.98831027391498, -37.81979489915996],
        [144.99005587754618, -37.81984019239263],
      ],
    ],
    { fillColor: 'blue', strokeColor: 'blue' }
  ),
];
const PIXEL_DIMS = { width: 1280, height: 720 };

const TEST_POLYGONS_2: GeoJson2SvgPolygonFeature[] = [
  {
      "type": "Feature",
      "geometry": {
          "type": "Polygon",
          "coordinates": [
              [
                  [
                      144.95978822541235,
                      -37.81568774820276
                  ],
                  [
                      144.96213478985817,
                      -37.81568774820276
                  ],
                  [
                      144.96213478985817,
                      -37.81426230220895
                  ],
                  [
                      144.95978822541235,
                      -37.81426230220895
                  ],
                  [
                      144.95978822541235,
                      -37.81568774820276
                  ],
                  [
                      144.95978822541235,
                      -37.81568774820276
                  ]
              ]
          ]
      },
      "properties": {
          "fillColor": "#a0a0a0",
          "strokeColor": "#000000",
          "fillOpacity": 0.24,
          "strokeOpacity": 0.6
      }
  },
  {
      "type": "Feature",
      "geometry": {
          "type": "Polygon",
          "coordinates": [
              [
                  [
                      144.960401133,
                      -37.815006122
                  ],
                  [
                      144.960417056,
                      -37.815012415
                  ],
                  [
                      144.960406545,
                      -37.815017962
                  ],
                  [
                      144.960406545,
                      -37.815019811
                  ],
                  [
                      144.960405617,
                      -37.8150203
                  ],
                  [
                      144.960405617,
                      -37.815066378
                  ],
                  [
                      144.960432521,
                      -37.815066378
                  ],
                  [
                      144.960432521,
                      -37.815105367
                  ],
                  [
                      144.960526684,
                      -37.815101822
                  ],
                  [
                      144.960531168,
                      -37.815133722
                  ],
                  [
                      144.960562556,
                      -37.815140811
                  ],
                  [
                      144.960571523,
                      -37.815176256
                  ],
                  [
                      144.960578719,
                      -37.8151791
                  ],
                  [
                      144.960590226,
                      -37.815209464
                  ],
                  [
                      144.960596415,
                      -37.815209464
                  ],
                  [
                      144.960607395,
                      -37.81525069
                  ],
                  [
                      144.960706042,
                      -37.815264868
                  ],
                  [
                      144.960706042,
                      -37.815270837
                  ],
                  [
                      144.960643987,
                      -37.815283936
                  ],
                  [
                      144.960643987,
                      -37.815296605
                  ],
                  [
                      144.960643267,
                      -37.815296768
                  ],
                  [
                      144.960643267,
                      -37.815349934
                  ],
                  [
                      144.960728462,
                      -37.815520068
                  ],
                  [
                      144.960730119,
                      -37.815532511
                  ],
                  [
                      144.960733588,
                      -37.815606651
                  ],
                  [
                      144.960742195,
                      -37.815623198
                  ],
                  [
                      144.960746397,
                      -37.815654757
                  ],
                  [
                      144.960763224,
                      -37.815663624
                  ],
                  [
                      144.960764948,
                      -37.815666938
                  ],
                  [
                      144.960764665,
                      -37.81567186
                  ],
                  [
                      144.960741913,
                      -37.815679568
                  ],
                  [
                      144.960741913,
                      -37.81568774820276
                  ],
                  [
                      144.96125666907065,
                      -37.81568774820276
                  ],
                  [
                      144.961248792,
                      -37.815645661
                  ],
                  [
                      144.961212952,
                      -37.815638568
                  ],
                  [
                      144.961210423,
                      -37.815632564
                  ],
                  [
                      144.961190308,
                      -37.815562601
                  ],
                  [
                      144.961180962,
                      -37.815562601
                  ],
                  [
                      144.961169504,
                      -37.81553539
                  ],
                  [
                      144.961167888,
                      -37.815520068
                  ],
                  [
                      144.961161442,
                      -37.815516246
                  ],
                  [
                      144.961142544,
                      -37.815471367
                  ],
                  [
                      144.961140985,
                      -37.815463357
                  ],
                  [
                      144.961139171,
                      -37.815463357
                  ],
                  [
                      144.961119457,
                      -37.815416542
                  ],
                  [
                      144.961114081,
                      -37.815385379
                  ],
                  [
                      144.961106334,
                      -37.815385379
                  ],
                  [
                      144.96105615,
                      -37.815266205
                  ],
                  [
                      144.96103375,
                      -37.815227195
                  ],
                  [
                      144.96099791,
                      -37.815209464
                  ],
                  [
                      144.96097103,
                      -37.815131444
                  ],
                  [
                      144.96097103,
                      -37.815071157
                  ],
                  [
                      144.960935189,
                      -37.815025054
                  ],
                  [
                      144.960935189,
                      -37.814993137
                  ],
                  [
                      144.960919508,
                      -37.814993137
                  ],
                  [
                      144.960911145,
                      -37.81495678
                  ],
                  [
                      144.960908309,
                      -37.814939942
                  ],
                  [
                      144.960733588,
                      -37.81493285
                  ],
                  [
                      144.960621587,
                      -37.814947035
                  ],
                  [
                      144.960618899,
                      -37.814955543
                  ],
                  [
                      144.960584975,
                      -37.814963588
                  ],
                  [
                      144.960573628,
                      -37.81497181
                  ],
                  [
                      144.960558866,
                      -37.814975406
                  ],
                  [
                      144.960554563,
                      -37.814985625
                  ],
                  [
                      144.96053734,
                      -37.814998105
                  ],
                  [
                      144.960487185,
                      -37.815003776
                  ],
                  [
                      144.960478512,
                      -37.815001488
                  ],
                  [
                      144.960473472,
                      -37.8149985
                  ],
                  [
                      144.960464785,
                      -37.814943489
                  ],
                  [
                      144.960402065,
                      -37.814947035
                  ],
                  [
                      144.960402065,
                      -37.814949354
                  ],
                  [
                      144.960401133,
                      -37.81494941
                  ],
                  [
                      144.960401133,
                      -37.815006122
                  ]
              ]
          ]
      },
      "properties": {
          "fillColor": "#3048c9",
          "strokeColor": "#3048c9"
      }
  },
  {
      "type": "Feature",
      "geometry": {
          "type": "MultiPolygon",
          "coordinates": [
              [
                  [
                      [
                          144.95978822541235,
                          -37.814675084
                      ],
                      [
                          144.959819044,
                          -37.814675084
                      ],
                      [
                          144.959810063,
                          -37.814590005
                      ],
                      [
                          144.95978822541235,
                          -37.81455798717169
                      ],
                      [
                          144.95978822541235,
                          -37.814675084
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.95978822541235,
                          -37.81454625977785
                      ],
                      [
                          144.959789766,
                          -37.814545956
                      ],
                      [
                          144.959790089,
                          -37.81454392
                      ],
                      [
                          144.959796592,
                          -37.81454392
                      ],
                      [
                          144.959796592,
                          -37.814517307
                      ],
                      [
                          144.959821202,
                          -37.814514071
                      ],
                      [
                          144.959821083,
                          -37.814512015
                      ],
                      [
                          144.959823534,
                          -37.814512015
                      ],
                      [
                          144.959832514,
                          -37.814455296
                      ],
                      [
                          144.959810063,
                          -37.814405666
                      ],
                      [
                          144.95978822541235,
                          -37.81440279250749
                      ],
                      [
                          144.95978822541235,
                          -37.81454625977785
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.960152727,
                          -37.81430018
                      ],
                      [
                          144.960193147,
                          -37.81431436
                      ],
                      [
                          144.960210964,
                          -37.814349518
                      ],
                      [
                          144.960207728,
                          -37.814363988
                      ],
                      [
                          144.960193147,
                          -37.814363988
                      ],
                      [
                          144.960193147,
                          -37.814410071
                      ],
                      [
                          144.960238829,
                          -37.814423183
                      ],
                      [
                          144.960241749,
                          -37.814436241
                      ],
                      [
                          144.960238058,
                          -37.814491603
                      ],
                      [
                          144.960269495,
                          -37.814491603
                      ],
                      [
                          144.960274766,
                          -37.814503044
                      ],
                      [
                          144.960276193,
                          -37.814519961
                      ],
                      [
                          144.960282372,
                          -37.814519554
                      ],
                      [
                          144.960287396,
                          -37.81453046
                      ],
                      [
                          144.960282969,
                          -37.81458985
                      ],
                      [
                          144.960283041,
                          -37.814589879
                      ],
                      [
                          144.960282968,
                          -37.81459086
                      ],
                      [
                          144.960309755,
                          -37.814601431
                      ],
                      [
                          144.960308449,
                          -37.814609162
                      ],
                      [
                          144.960300933,
                          -37.814612129
                      ],
                      [
                          144.960300933,
                          -37.814651122
                      ],
                      [
                          144.960301337,
                          -37.814651255
                      ],
                      [
                          144.960300933,
                          -37.814653643
                      ],
                      [
                          144.960349688,
                          -37.814669674
                      ],
                      [
                          144.960355965,
                          -37.81468125
                      ],
                      [
                          144.960359317,
                          -37.814710348
                      ],
                      [
                          144.960369621,
                          -37.814718478
                      ],
                      [
                          144.96037279,
                          -37.814729109
                      ],
                      [
                          144.960383436,
                          -37.81473191
                      ],
                      [
                          144.960383835,
                          -37.814732646
                      ],
                      [
                          144.960386263,
                          -37.814752473
                      ],
                      [
                          144.960386263,
                          -37.814821275
                      ],
                      [
                          144.960396706,
                          -37.814837761
                      ],
                      [
                          144.960397291,
                          -37.814842532
                      ],
                      [
                          144.96042425,
                          -37.814847859
                      ],
                      [
                          144.960424511,
                          -37.814848019
                      ],
                      [
                          144.960426023,
                          -37.814849214
                      ],
                      [
                          144.960424021,
                          -37.814858695
                      ],
                      [
                          144.960410654,
                          -37.814871001
                      ],
                      [
                          144.960403659,
                          -37.81487321
                      ],
                      [
                          144.960448095,
                          -37.81487321
                      ],
                      [
                          144.960448095,
                          -37.81489448
                      ],
                      [
                          144.960528884,
                          -37.814890935
                      ],
                      [
                          144.960537861,
                          -37.81490866
                      ],
                      [
                          144.960551326,
                          -37.81490157
                      ],
                      [
                          144.960542349,
                          -37.814848394
                      ],
                      [
                          144.960506443,
                          -37.814848394
                      ],
                      [
                          144.960506443,
                          -37.814809399
                      ],
                      [
                          144.960555814,
                          -37.814809399
                      ],
                      [
                          144.960555814,
                          -37.814844849
                      ],
                      [
                          144.960573767,
                          -37.814823579
                      ],
                      [
                          144.96061865,
                          -37.814816489
                      ],
                      [
                          144.960560303,
                          -37.814802308
                      ],
                      [
                          144.960560303,
                          -37.814745587
                      ],
                      [
                          144.960578256,
                          -37.814745587
                      ],
                      [
                          144.960578256,
                          -37.814731407
                      ],
                      [
                          144.960632115,
                          -37.814731407
                      ],
                      [
                          144.960632115,
                          -37.814752677
                      ],
                      [
                          144.960654557,
                          -37.814752677
                      ],
                      [
                          144.960641092,
                          -37.814809399
                      ],
                      [
                          144.960654557,
                          -37.814795218
                      ],
                      [
                          144.960775741,
                          -37.814784583
                      ],
                      [
                          144.960779269,
                          -37.814792944
                      ],
                      [
                          144.960780594,
                          -37.814792315
                      ],
                      [
                          144.960797685,
                          -37.814789743
                      ],
                      [
                          144.960843268,
                          -37.814789743
                      ],
                      [
                          144.960843268,
                          -37.814782882
                      ],
                      [
                          144.960862499,
                          -37.814779987
                      ],
                      [
                          144.960879132,
                          -37.814779987
                      ],
                      [
                          144.960847553,
                          -37.814777493
                      ],
                      [
                          144.960847553,
                          -37.814724317
                      ],
                      [
                          144.960864503,
                          -37.814724317
                      ],
                      [
                          144.960854887,
                          -37.81471064
                      ],
                      [
                          144.960863741,
                          -37.814654661
                      ],
                      [
                          144.9608614,
                          -37.814654045
                      ],
                      [
                          144.960861419,
                          -37.814653932
                      ],
                      [
                          144.960820736,
                          -37.814634643
                      ],
                      [
                          144.960814405,
                          -37.814612124
                      ],
                      [
                          144.960813271,
                          -37.814612124
                      ],
                      [
                          144.960812026,
                          -37.814604256
                      ],
                      [
                          144.960786589,
                          -37.814604256
                      ],
                      [
                          144.960785084,
                          -37.814591174
                      ],
                      [
                          144.960785084,
                          -37.814572321
                      ],
                      [
                          144.960735692,
                          -37.814572321
                      ],
                      [
                          144.960735692,
                          -37.814533289
                      ],
                      [
                          144.960733673,
                          -37.814532492
                      ],
                      [
                          144.960733673,
                          -37.814530595
                      ],
                      [
                          144.960696694,
                          -37.814511111
                      ],
                      [
                          144.960691012,
                          -37.814496357
                      ],
                      [
                          144.960693307,
                          -37.814495148
                      ],
                      [
                          144.960688822,
                          -37.814449066
                      ],
                      [
                          144.960661911,
                          -37.814431342
                      ],
                      [
                          144.960636545,
                          -37.814432774
                      ],
                      [
                          144.960596494,
                          -37.81442684
                      ],
                      [
                          144.960596494,
                          -37.814409517
                      ],
                      [
                          144.960601257,
                          -37.814409098
                      ],
                      [
                          144.960632416,
                          -37.814409098
                      ],
                      [
                          144.960641873,
                          -37.814358652
                      ],
                      [
                          144.960652941,
                          -37.814356903
                      ],
                      [
                          144.960652941,
                          -37.81431791
                      ],
                      [
                          144.960649703,
                          -37.814316887
                      ],
                      [
                          144.960650377,
                          -37.814313294
                      ],
                      [
                          144.960624013,
                          -37.814308765
                      ],
                      [
                          144.96060809,
                          -37.814303731
                      ],
                      [
                          144.960609812,
                          -37.814272426
                      ],
                      [
                          144.960614455,
                          -37.814270714
                      ],
                      [
                          144.960614455,
                          -37.81426230220895
                      ],
                      [
                          144.960152727,
                          -37.81426230220895
                      ],
                      [
                          144.960152727,
                          -37.81430018
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.960865721,
                          -37.814724317
                      ],
                      [
                          144.960892436,
                          -37.814724317
                      ],
                      [
                          144.960901413,
                          -37.814710137
                      ],
                      [
                          144.960995667,
                          -37.814720772
                      ],
                      [
                          144.961001394,
                          -37.814744899
                      ],
                      [
                          144.961011169,
                          -37.81474209
                      ],
                      [
                          144.96101362,
                          -37.814699501
                      ],
                      [
                          144.961166222,
                          -37.814653416
                      ],
                      [
                          144.961178194,
                          -37.814628199
                      ],
                      [
                          144.961153054,
                          -37.814630004
                      ],
                      [
                          144.961148632,
                          -37.814630179
                      ],
                      [
                          144.961148233,
                          -37.814630351
                      ],
                      [
                          144.961139743,
                          -37.81463096
                      ],
                      [
                          144.961139743,
                          -37.814634007
                      ],
                      [
                          144.961113864,
                          -37.814645153
                      ],
                      [
                          144.961094832,
                          -37.814645153
                      ],
                      [
                          144.961027466,
                          -37.814677088
                      ],
                      [
                          144.960919681,
                          -37.814694829
                      ],
                      [
                          144.960915681,
                          -37.81470115
                      ],
                      [
                          144.960878522,
                          -37.814709022
                      ],
                      [
                          144.960865789,
                          -37.814709022
                      ],
                      [
                          144.960865789,
                          -37.81471172
                      ],
                      [
                          144.960865721,
                          -37.814711734
                      ],
                      [
                          144.960865721,
                          -37.814724317
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.960914878,
                          -37.814973007
                      ],
                      [
                          144.960919508,
                          -37.814993137
                      ],
                      [
                          144.960935189,
                          -37.814993137
                      ],
                      [
                          144.960935189,
                          -37.815025054
                      ],
                      [
                          144.960942144,
                          -37.815034001
                      ],
                      [
                          144.960996029,
                          -37.815039009
                      ],
                      [
                          144.960999626,
                          -37.815037881
                      ],
                      [
                          144.96100044,
                          -37.815037953
                      ],
                      [
                          144.961280665,
                          -37.814950148
                      ],
                      [
                          144.961395733,
                          -37.814939658
                      ],
                      [
                          144.961449625,
                          -37.814921917
                      ],
                      [
                          144.961450424,
                          -37.814918764
                      ],
                      [
                          144.961457075,
                          -37.814903007
                      ],
                      [
                          144.961466762,
                          -37.81489918
                      ],
                      [
                          144.961531138,
                          -37.814895415
                      ],
                      [
                          144.961575375,
                          -37.814897079
                      ],
                      [
                          144.961577593,
                          -37.814892698
                      ],
                      [
                          144.961579734,
                          -37.814892573
                      ],
                      [
                          144.961597697,
                          -37.814867752
                      ],
                      [
                          144.961653416,
                          -37.814842611
                      ],
                      [
                          144.961660705,
                          -37.814840307
                      ],
                      [
                          144.96172358,
                          -37.814850952
                      ],
                      [
                          144.961729746,
                          -37.814848029
                      ],
                      [
                          144.961745888,
                          -37.814842931
                      ],
                      [
                          144.961746572,
                          -37.814838611
                      ],
                      [
                          144.961754241,
                          -37.814814373
                      ],
                      [
                          144.961822229,
                          -37.814811018
                      ],
                      [
                          144.961839844,
                          -37.814805539
                      ],
                      [
                          144.961849329,
                          -37.814804825
                      ],
                      [
                          144.961851092,
                          -37.81480204
                      ],
                      [
                          144.96197042,
                          -37.814764922
                      ],
                      [
                          144.961999044,
                          -37.814745146
                      ],
                      [
                          144.962017462,
                          -37.814739193
                      ],
                      [
                          144.96213478985817,
                          -37.81472960874266
                      ],
                      [
                          144.96213478985817,
                          -37.814624282
                      ],
                      [
                          144.962109467,
                          -37.814624282
                      ],
                      [
                          144.962082548,
                          -37.814649067
                      ],
                      [
                          144.961952444,
                          -37.81466677
                      ],
                      [
                          144.961885149,
                          -37.814688014
                      ],
                      [
                          144.961885149,
                          -37.814702177
                      ],
                      [
                          144.961719154,
                          -37.814734043
                      ],
                      [
                          144.961687749,
                          -37.814769449
                      ],
                      [
                          144.961629426,
                          -37.814776531
                      ],
                      [
                          144.961615967,
                          -37.814797774
                      ],
                      [
                          144.961499322,
                          -37.814797774
                      ],
                      [
                          144.961453434,
                          -37.814795188
                      ],
                      [
                          144.961462449,
                          -37.814802308
                      ],
                      [
                          144.961462449,
                          -37.814844849
                      ],
                      [
                          144.961448984,
                          -37.814844849
                      ],
                      [
                          144.961448984,
                          -37.81487321
                      ],
                      [
                          144.96138166,
                          -37.814876755
                      ],
                      [
                          144.961377172,
                          -37.814834214
                      ],
                      [
                          144.96135473,
                          -37.814837759
                      ],
                      [
                          144.961309847,
                          -37.814855484
                      ],
                      [
                          144.961323312,
                          -37.814862574
                      ],
                      [
                          144.961323312,
                          -37.81490157
                      ],
                      [
                          144.961305359,
                          -37.814912205
                      ],
                      [
                          144.96119764,
                          -37.81490866
                      ],
                      [
                          144.961166222,
                          -37.814919295
                      ],
                      [
                          144.961166222,
                          -37.814947656
                      ],
                      [
                          144.961107874,
                          -37.814951201
                      ],
                      [
                          144.961094409,
                          -37.814972471
                      ],
                      [
                          144.96104055,
                          -37.814972471
                      ],
                      [
                          144.961022597,
                          -37.814937021
                      ],
                      [
                          144.960946296,
                          -37.814933476
                      ],
                      [
                          144.960941807,
                          -37.814965381
                      ],
                      [
                          144.960914878,
                          -37.814968926
                      ],
                      [
                          144.960914878,
                          -37.814973007
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.96100359,
                          -37.814754149
                      ],
                      [
                          144.961009132,
                          -37.814777493
                      ],
                      [
                          144.961010513,
                          -37.814753492
                      ],
                      [
                          144.96100359,
                          -37.814754149
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.961036148,
                          -37.814316372
                      ],
                      [
                          144.961085512,
                          -37.814316372
                      ],
                      [
                          144.961233606,
                          -37.814270259
                      ],
                      [
                          144.961247069,
                          -37.814270259
                      ],
                      [
                          144.961256045,
                          -37.814298636
                      ],
                      [
                          144.961359262,
                          -37.814309278
                      ],
                      [
                          144.961359262,
                          -37.814306176
                      ],
                      [
                          144.961360062,
                          -37.814306286
                      ],
                      [
                          144.961360062,
                          -37.81426230220895
                      ],
                      [
                          144.96104507060244,
                          -37.81426230220895
                      ],
                      [
                          144.961036148,
                          -37.814316372
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.961194063,
                          -37.814627059
                      ],
                      [
                          144.961241011,
                          -37.814633603
                      ],
                      [
                          144.961238546,
                          -37.814623864
                      ],
                      [
                          144.961194063,
                          -37.814627059
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.96128015,
                          -37.815392262
                      ],
                      [
                          144.961313794,
                          -37.815455471
                      ],
                      [
                          144.961304588,
                          -37.815460392
                      ],
                      [
                          144.961304588,
                          -37.815499275
                      ],
                      [
                          144.961311969,
                          -37.815515058
                      ],
                      [
                          144.961311413,
                          -37.815522982
                      ],
                      [
                          144.961316058,
                          -37.815523799
                      ],
                      [
                          144.961317813,
                          -37.815527553
                      ],
                      [
                          144.961337414,
                          -37.815527553
                      ],
                      [
                          144.961349326,
                          -37.815529647
                      ],
                      [
                          144.961357491,
                          -37.815555832
                      ],
                      [
                          144.961304588,
                          -37.815562901
                      ],
                      [
                          144.961300179,
                          -37.815608854
                      ],
                      [
                          144.961317813,
                          -37.815640667
                      ],
                      [
                          144.961397168,
                          -37.815640667
                      ],
                      [
                          144.96141837201387,
                          -37.81568774820276
                      ],
                      [
                          144.96153651531029,
                          -37.81568774820276
                      ],
                      [
                          144.961528189,
                          -37.815663284
                      ],
                      [
                          144.961525016,
                          -37.815644202
                      ],
                      [
                          144.961498565,
                          -37.815608854
                      ],
                      [
                          144.961485339,
                          -37.815548762
                      ],
                      [
                          144.961481541,
                          -37.815546478
                      ],
                      [
                          144.961481126,
                          -37.81554418
                      ],
                      [
                          144.96147506,
                          -37.815542581
                      ],
                      [
                          144.961467705,
                          -37.815538158
                      ],
                      [
                          144.961467705,
                          -37.815506344
                      ],
                      [
                          144.961454626,
                          -37.81548537
                      ],
                      [
                          144.961454329,
                          -37.815484119
                      ],
                      [
                          144.961400735,
                          -37.815395795
                      ],
                      [
                          144.961400735,
                          -37.815374597
                      ],
                      [
                          144.961445397,
                          -37.815367531
                      ],
                      [
                          144.961445397,
                          -37.815328668
                      ],
                      [
                          144.961391803,
                          -37.815328668
                      ],
                      [
                          144.961373939,
                          -37.815332201
                      ],
                      [
                          144.961373939,
                          -37.815346333
                      ],
                      [
                          144.96128015,
                          -37.815353399
                      ],
                      [
                          144.96128015,
                          -37.815392262
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.961323312,
                          -37.814656961
                      ],
                      [
                          144.961374494,
                          -37.814664541
                      ],
                      [
                          144.96139165,
                          -37.814649067
                      ],
                      [
                          144.96142754,
                          -37.814649067
                      ],
                      [
                          144.961387163,
                          -37.814641986
                      ],
                      [
                          144.961387163,
                          -37.814632032
                      ],
                      [
                          144.961386751,
                          -37.814631969
                      ],
                      [
                          144.961386751,
                          -37.814620316
                      ],
                      [
                          144.961332858,
                          -37.814620316
                      ],
                      [
                          144.961331482,
                          -37.814623577
                      ],
                      [
                          144.961328258,
                          -37.814623087
                      ],
                      [
                          144.961323312,
                          -37.814633826
                      ],
                      [
                          144.961323312,
                          -37.814656961
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.961571862,
                          -37.814601941
                      ],
                      [
                          144.961593536,
                          -37.814599498
                      ],
                      [
                          144.961580077,
                          -37.814585335
                      ],
                      [
                          144.961580077,
                          -37.814571712
                      ],
                      [
                          144.961575375,
                          -37.814574188
                      ],
                      [
                          144.961571862,
                          -37.814601941
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.961629131,
                          -37.814525208
                      ],
                      [
                          144.961678776,
                          -37.814514522
                      ],
                      [
                          144.961680019,
                          -37.814509619
                      ],
                      [
                          144.961642603,
                          -37.814509619
                      ],
                      [
                          144.961629131,
                          -37.814513165
                      ],
                      [
                          144.961629131,
                          -37.814525208
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.962113953,
                          -37.814436627
                      ],
                      [
                          144.962118439,
                          -37.814454331
                      ],
                      [
                          144.96213478985817,
                          -37.81444787876394
                      ],
                      [
                          144.96213478985817,
                          -37.81443372500824
                      ],
                      [
                          144.962113953,
                          -37.814436627
                      ]
                  ]
              ]
          ]
      },
      "properties": {
          "fillColor": "#3048c9",
          "strokeColor": "#3048c9"
      }
  },
  {
      "type": "Feature",
      "geometry": {
          "type": "MultiPolygon",
          "coordinates": [
              [
                  [
                      [
                          144.95978822541235,
                          -37.81542234836038
                      ],
                      [
                          144.959805904,
                          -37.815380482
                      ],
                      [
                          144.959810393,
                          -37.81539466
                      ],
                      [
                          144.959891212,
                          -37.815391115
                      ],
                      [
                          144.959909171,
                          -37.815384026
                      ],
                      [
                          144.959909171,
                          -37.815355672
                      ],
                      [
                          144.95995856,
                          -37.815373393
                      ],
                      [
                          144.95996754,
                          -37.815405293
                      ],
                      [
                          144.960025909,
                          -37.815405293
                      ],
                      [
                          144.960048358,
                          -37.815387571
                      ],
                      [
                          144.960111217,
                          -37.815380482
                      ],
                      [
                          144.960124686,
                          -37.815352127
                      ],
                      [
                          144.960124686,
                          -37.815313139
                      ],
                      [
                          144.960066318,
                          -37.815313139
                      ],
                      [
                          144.960052848,
                          -37.815334405
                      ],
                      [
                          144.960030399,
                          -37.815334405
                      ],
                      [
                          144.960043868,
                          -37.81530605
                      ],
                      [
                          144.960151626,
                          -37.81528124
                      ],
                      [
                          144.960183055,
                          -37.815288329
                      ],
                      [
                          144.960183055,
                          -37.815341494
                      ],
                      [
                          144.960241424,
                          -37.815341494
                      ],
                      [
                          144.960335712,
                          -37.815313139
                      ],
                      [
                          144.960335712,
                          -37.815259974
                      ],
                      [
                          144.960394081,
                          -37.815256429
                      ],
                      [
                          144.960394081,
                          -37.815220986
                      ],
                      [
                          144.96041204,
                          -37.815203264
                      ],
                      [
                          144.960537757,
                          -37.815164276
                      ],
                      [
                          144.960569061,
                          -37.815166522
                      ],
                      [
                          144.960562556,
                          -37.815140811
                      ],
                      [
                          144.960531168,
                          -37.815133722
                      ],
                      [
                          144.960526684,
                          -37.815101822
                      ],
                      [
                          144.960432521,
                          -37.815105367
                      ],
                      [
                          144.960432521,
                          -37.815066378
                      ],
                      [
                          144.960405617,
                          -37.815066378
                      ],
                      [
                          144.960405617,
                          -37.8150203
                      ],
                      [
                          144.960406545,
                          -37.815019811
                      ],
                      [
                          144.960406545,
                          -37.815017962
                      ],
                      [
                          144.960417056,
                          -37.815012415
                      ],
                      [
                          144.960401133,
                          -37.815006122
                      ],
                      [
                          144.960401133,
                          -37.81494941
                      ],
                      [
                          144.960402065,
                          -37.814949354
                      ],
                      [
                          144.960402065,
                          -37.814947035
                      ],
                      [
                          144.960464785,
                          -37.814943489
                      ],
                      [
                          144.960473472,
                          -37.8149985
                      ],
                      [
                          144.960478512,
                          -37.815001488
                      ],
                      [
                          144.960487185,
                          -37.815003776
                      ],
                      [
                          144.96053734,
                          -37.814998105
                      ],
                      [
                          144.960554563,
                          -37.814985625
                      ],
                      [
                          144.960558866,
                          -37.814975406
                      ],
                      [
                          144.960573628,
                          -37.81497181
                      ],
                      [
                          144.960584975,
                          -37.814963588
                      ],
                      [
                          144.960618899,
                          -37.814955543
                      ],
                      [
                          144.960621587,
                          -37.814947035
                      ],
                      [
                          144.960733588,
                          -37.81493285
                      ],
                      [
                          144.960908309,
                          -37.814939942
                      ],
                      [
                          144.960911145,
                          -37.81495678
                      ],
                      [
                          144.960914878,
                          -37.814973007
                      ],
                      [
                          144.960914878,
                          -37.814968926
                      ],
                      [
                          144.960941807,
                          -37.814965381
                      ],
                      [
                          144.960946296,
                          -37.814933476
                      ],
                      [
                          144.961022597,
                          -37.814937021
                      ],
                      [
                          144.96104055,
                          -37.814972471
                      ],
                      [
                          144.961094409,
                          -37.814972471
                      ],
                      [
                          144.961107874,
                          -37.814951201
                      ],
                      [
                          144.961166222,
                          -37.814947656
                      ],
                      [
                          144.961166222,
                          -37.814919295
                      ],
                      [
                          144.96119764,
                          -37.81490866
                      ],
                      [
                          144.961305359,
                          -37.814912205
                      ],
                      [
                          144.961323312,
                          -37.81490157
                      ],
                      [
                          144.961323312,
                          -37.814862574
                      ],
                      [
                          144.961309847,
                          -37.814855484
                      ],
                      [
                          144.96135473,
                          -37.814837759
                      ],
                      [
                          144.961377172,
                          -37.814834214
                      ],
                      [
                          144.96138166,
                          -37.814876755
                      ],
                      [
                          144.961448984,
                          -37.81487321
                      ],
                      [
                          144.961448984,
                          -37.814844849
                      ],
                      [
                          144.961462449,
                          -37.814844849
                      ],
                      [
                          144.961462449,
                          -37.814802308
                      ],
                      [
                          144.961453434,
                          -37.814795188
                      ],
                      [
                          144.961499322,
                          -37.814797774
                      ],
                      [
                          144.961615967,
                          -37.814797774
                      ],
                      [
                          144.961629426,
                          -37.814776531
                      ],
                      [
                          144.961687749,
                          -37.814769449
                      ],
                      [
                          144.961719154,
                          -37.814734043
                      ],
                      [
                          144.961885149,
                          -37.814702177
                      ],
                      [
                          144.961885149,
                          -37.814688014
                      ],
                      [
                          144.961952444,
                          -37.81466677
                      ],
                      [
                          144.962082548,
                          -37.814649067
                      ],
                      [
                          144.962109467,
                          -37.814624282
                      ],
                      [
                          144.96213478985817,
                          -37.814624282
                      ],
                      [
                          144.96213478985817,
                          -37.81444787876394
                      ],
                      [
                          144.962118439,
                          -37.814454331
                      ],
                      [
                          144.962113953,
                          -37.814436627
                      ],
                      [
                          144.96213478985817,
                          -37.81443372500824
                      ],
                      [
                          144.96213478985817,
                          -37.81437136100707
                      ],
                      [
                          144.962100494,
                          -37.814376436
                      ],
                      [
                          144.962073576,
                          -37.814401221
                      ],
                      [
                          144.961961417,
                          -37.814411843
                      ],
                      [
                          144.961961417,
                          -37.814426005
                      ],
                      [
                          144.961925526,
                          -37.814440168
                      ],
                      [
                          144.961835799,
                          -37.81445079
                      ],
                      [
                          144.961835799,
                          -37.814468493
                      ],
                      [
                          144.961790935,
                          -37.814479115
                      ],
                      [
                          144.961755044,
                          -37.814479115
                      ],
                      [
                          144.961750558,
                          -37.814461412
                      ],
                      [
                          144.961692235,
                          -37.814461412
                      ],
                      [
                          144.961678776,
                          -37.814514522
                      ],
                      [
                          144.961580077,
                          -37.814535766
                      ],
                      [
                          144.961580077,
                          -37.814585335
                      ],
                      [
                          144.961593536,
                          -37.814599498
                      ],
                      [
                          144.961562131,
                          -37.814603039
                      ],
                      [
                          144.961562131,
                          -37.81455701
                      ],
                      [
                          144.961508295,
                          -37.81455701
                      ],
                      [
                          144.961458945,
                          -37.814567632
                      ],
                      [
                          144.961458945,
                          -37.814585335
                      ],
                      [
                          144.961400622,
                          -37.814581795
                      ],
                      [
                          144.961387163,
                          -37.814603039
                      ],
                      [
                          144.961387163,
                          -37.814641986
                      ],
                      [
                          144.96142754,
                          -37.814649067
                      ],
                      [
                          144.96139165,
                          -37.814649067
                      ],
                      [
                          144.961374494,
                          -37.814664541
                      ],
                      [
                          144.961323312,
                          -37.814656961
                      ],
                      [
                          144.961323312,
                          -37.814617965
                      ],
                      [
                          144.961269453,
                          -37.814617965
                      ],
                      [
                          144.961255988,
                          -37.81463569
                      ],
                      [
                          144.961179687,
                          -37.814625055
                      ],
                      [
                          144.961166222,
                          -37.814653416
                      ],
                      [
                          144.96101362,
                          -37.814699501
                      ],
                      [
                          144.961009132,
                          -37.814777493
                      ],
                      [
                          144.960995667,
                          -37.814720772
                      ],
                      [
                          144.960901413,
                          -37.814710137
                      ],
                      [
                          144.960892436,
                          -37.814724317
                      ],
                      [
                          144.960847553,
                          -37.814724317
                      ],
                      [
                          144.960847553,
                          -37.814777493
                      ],
                      [
                          144.960892436,
                          -37.814781038
                      ],
                      [
                          144.960816135,
                          -37.814795218
                      ],
                      [
                          144.960816135,
                          -37.814809399
                      ],
                      [
                          144.960784717,
                          -37.814805853
                      ],
                      [
                          144.960775741,
                          -37.814784583
                      ],
                      [
                          144.960654557,
                          -37.814795218
                      ],
                      [
                          144.960641092,
                          -37.814809399
                      ],
                      [
                          144.960654557,
                          -37.814752677
                      ],
                      [
                          144.960632115,
                          -37.814752677
                      ],
                      [
                          144.960632115,
                          -37.814731407
                      ],
                      [
                          144.960578256,
                          -37.814731407
                      ],
                      [
                          144.960578256,
                          -37.814745587
                      ],
                      [
                          144.960560303,
                          -37.814745587
                      ],
                      [
                          144.960560303,
                          -37.814802308
                      ],
                      [
                          144.96061865,
                          -37.814816489
                      ],
                      [
                          144.960573767,
                          -37.814823579
                      ],
                      [
                          144.960555814,
                          -37.814844849
                      ],
                      [
                          144.960555814,
                          -37.814809399
                      ],
                      [
                          144.960506443,
                          -37.814809399
                      ],
                      [
                          144.960506443,
                          -37.814848394
                      ],
                      [
                          144.960542349,
                          -37.814848394
                      ],
                      [
                          144.960551326,
                          -37.81490157
                      ],
                      [
                          144.960537861,
                          -37.81490866
                      ],
                      [
                          144.960528884,
                          -37.814890935
                      ],
                      [
                          144.960448095,
                          -37.81489448
                      ],
                      [
                          144.960448095,
                          -37.81487321
                      ],
                      [
                          144.960398724,
                          -37.81487321
                      ],
                      [
                          144.96025061,
                          -37.81492284
                      ],
                      [
                          144.960237146,
                          -37.81492284
                      ],
                      [
                          144.960237146,
                          -37.814905115
                      ],
                      [
                          144.960165333,
                          -37.81490866
                      ],
                      [
                          144.960160845,
                          -37.814937021
                      ],
                      [
                          144.960098008,
                          -37.814940566
                      ],
                      [
                          144.960094484,
                          -37.814948918
                      ],
                      [
                          144.960053227,
                          -37.814963721
                      ],
                      [
                          144.960021708,
                          -37.814965381
                      ],
                      [
                          144.96001163,
                          -37.814978648
                      ],
                      [
                          144.960007949,
                          -37.814979968
                      ],
                      [
                          144.960007949,
                          -37.814983106
                      ],
                      [
                          144.959864617,
                          -37.814983106
                      ],
                      [
                          144.959846664,
                          -37.815015012
                      ],
                      [
                          144.959824223,
                          -37.815015012
                      ],
                      [
                          144.959815246,
                          -37.815046917
                      ],
                      [
                          144.95978822541235,
                          -37.815046917
                      ],
                      [
                          144.95978822541235,
                          -37.81542234836038
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.96171649451216,
                          -37.81568774820276
                      ],
                      [
                          144.96178123141834,
                          -37.81568774820276
                      ],
                      [
                          144.961719902,
                          -37.815676975
                      ],
                      [
                          144.96171649451216,
                          -37.81568774820276
                      ]
                  ]
              ],
              [
                  [
                      [
                          144.96178390817346,
                          -37.81568774820276
                      ],
                      [
                          144.96213478985817,
                          -37.81568774820276
                      ],
                      [
                          144.96213478985817,
                          -37.815502696337745
                      ],
                      [
                          144.962123868,
                          -37.815503128
                      ],
                      [
                          144.962112775,
                          -37.815506635
                      ],
                      [
                          144.962112246,
                          -37.815506672
                      ],
                      [
                          144.962112223,
                          -37.81550681
                      ],
                      [
                          144.962101426,
                          -37.815510223
                      ],
                      [
                          144.962101426,
                          -37.815552798
                      ],
                      [
                          144.962103837,
                          -37.815556611
                      ],
                      [
                          144.962103285,
                          -37.81555989
                      ],
                      [
                          144.962116654,
                          -37.815581062
                      ],
                      [
                          144.962115751,
                          -37.815581022
                      ],
                      [
                          144.962049522,
                          -37.815570533
                      ],
                      [
                          144.962043637,
                          -37.815577856
                      ],
                      [
                          144.962038587,
                          -37.815577634
                      ],
                      [
                          144.962031051,
                          -37.815593518
                      ],
                      [
                          144.962020229,
                          -37.815606984
                      ],
                      [
                          144.961972391,
                          -37.815616437
                      ],
                      [
                          144.96196995,
                          -37.815615885
                      ],
                      [
                          144.961941994,
                          -37.815599312
                      ],
                      [
                          144.961941994,
                          -37.815595368
                      ],
                      [
                          144.961865829,
                          -37.815595368
                      ],
                      [
                          144.961865827,
                          -37.815595373
                      ],
                      [
                          144.961859046,
                          -37.815595373
                      ],
                      [
                          144.961845387,
                          -37.815645038
                      ],
                      [
                          144.961834467,
                          -37.815645038
                      ],
                      [
                          144.961834467,
                          -37.815676969
                      ],
                      [
                          144.96178390817346,
                          -37.81568774820276
                      ]
                  ]
              ]
          ]
      },
      "properties": {
          "fillColor": "#3048c9",
          "strokeColor": "#3048c9"
      }
  }
];

const TEST_LABELS_2 = [
  {
    latLng: [144.960861741, -37.815323351],
    label: 'Feb 2019',
    displayColor: '#3048c9',
  },
  {
    latLng: [144.959808125, -37.814483655],
    label: 'Jul 2016',
    displayColor: '#3048c9',
  },
  {
    latLng: [144.961016019, -37.814685958],
    label: 'Jul 2016',
    displayColor: '#3048c9',
  },
  {
    latLng: [144.961114727, -37.814284448],
    label: 'Jul 2016',
    displayColor: '#3048c9',
  },
  {
    latLng: [144.961388818, -37.815510701],
    label: 'Jul 2016',
    displayColor: '#3048c9',
  },
  {
    latLng: [144.961606776, -37.814824294],
    label: 'Jul 2016',
    displayColor: '#3048c9',
  },
  {
    latLng: [144.960534353, -37.814581086],
    label: 'Jul 2016',
    displayColor: '#3048c9',
  },
  {
    latLng: [144.962082549, -37.815594443],
    label: 'Oct 2017',
    displayColor: '#3048c9',
  },
  {
    latLng: [144.96093702, -37.814898025],
    label: 'Oct 2017',
    displayColor: '#3048c9',
  },
];
